import React from 'react';
import { Card, Alert, Button, ListGroup } from 'react-bootstrap';
import { get_json, buildRssUrl } from './util.js';

class RSSFeedView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      items: [],
      isLoading: true,
      error: null
    };
  }

  componentDidMount() {
    this.loadFeed();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.url !== this.props.url) {
      this.loadFeed();
    }
  }

  loadFeed = () => {
    this.setState({ isLoading: true, error: null });
    
    // Add preview parameter if in preview mode
    const url = this.props.preview ? `${this.props.url}?preview=true` : this.props.url;
    
    // Use buildRssUrl to ensure correct port
    fetch(buildRssUrl(url))
      .then(response => response.text())
      .then(str => new window.DOMParser().parseFromString(str, "text/xml"))
      .then(data => {
        const items = Array.from(data.querySelectorAll('item')).map(item => ({
          title: item.querySelector('title')?.textContent || '',
          link: item.querySelector('link')?.textContent || '',
          description: item.querySelector('description')?.textContent || '',
          pubDate: item.querySelector('pubDate')?.textContent || '',
          guid: item.querySelector('guid')?.textContent || ''
        }));
        this.setState({ items, isLoading: false });
      })
      .catch(error => {
        this.setState({ error: "Failed to load RSS feed", isLoading: false });
      });
  }

  render() {
    const { items, isLoading, error } = this.state;

    if (isLoading) {
      return (
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <Alert variant="danger" className="m-3">
          {error}
        </Alert>
      );
    }

    return (
      <div className="feed-items">
        {items.length === 0 ? (
          <div className="text-center p-5 text-muted">
            <p className="mb-0">No items found in this feed</p>
          </div>
        ) : (
          <div className="p-3">
            {items.map((item, index) => (
              <div key={item.guid || index} className="feed-item mb-4 pb-4 border-bottom">
                <h6 className="mb-2">
                  <a 
                    href={item.link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-decoration-none"
                  >
                    {item.title}
                  </a>
                </h6>
                <div className="small text-muted mb-2">
                  {new Date(item.pubDate).toLocaleString()}
                </div>
                <div className="feed-description small">
                  {item.description}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
}

class Endpoints extends React.Component {
  constructor() {
    super();
    this.state = {
      endpoints: [],
      selectedEndpoint: null,
      isLoading: true,
      error: null,
      commitInProgress: false,
      previewMode: true  // Default to preview mode
    };
    this.feedViewRef = React.createRef();
  }

  componentDidMount() {
    this.loadEndpoints();
  }

  loadEndpoints = () => {
    get_json('/endpoint/list', (response) => {
      if (response.error) {
        this.setState({ error: response.error, isLoading: false });
        return;
      }
      this.setState({ 
        endpoints: response,
        isLoading: false 
      });
    });
  }

  handleCommit = () => {
    const { selectedEndpoint } = this.state;
    if (!selectedEndpoint) return;

    this.setState({ commitInProgress: true });

    if (this.feedViewRef.current) {
      this.feedViewRef.current.setState({ isLoading: true, error: null });
    }

    fetch(buildRssUrl(selectedEndpoint))
      .then(response => response.text())
      .then(str => {
        const doc = new window.DOMParser().parseFromString(str, "text/xml");
        const items = Array.from(doc.querySelectorAll('item')).map(item => ({
          title: item.querySelector('title')?.textContent || '',
          link: item.querySelector('link')?.textContent || '',
          description: item.querySelector('description')?.textContent || '',
          pubDate: item.querySelector('pubDate')?.textContent || '',
          guid: item.querySelector('guid')?.textContent || ''
        }));
        
        this.setState({ previewMode: false, commitInProgress: false });
        if (this.feedViewRef.current) {
          this.feedViewRef.current.setState({ items, isLoading: false });
        }
      })
      .catch(error => {
        this.setState({ error: "Failed to commit changes", commitInProgress: false });
        if (this.feedViewRef.current) {
          this.feedViewRef.current.setState({ error: "Failed to load RSS feed", isLoading: false });
        }
      });
  }

  handleEndpointSelect = (endpoint) => {
    this.setState({ 
      selectedEndpoint: endpoint,
      previewMode: this.isUniqueEndpoint(endpoint)  // Enable preview mode for unique endpoints
    });
  }

  // Group endpoints by name and type (source/filter)
  groupEndpoints = (endpoints) => {
    const groups = {
      source: {},
      filter: {}
    };
    
    endpoints.forEach(endpoint => {
      const type = endpoint.urls.all.split('/')[3]; // 'source' or 'filter'
      const name = endpoint.name;
      
      if (!groups[type]) {
        groups[type] = {};
      }
      
      groups[type][name] = endpoint;
    });
    
    return groups;
  }

  isUniqueEndpoint = (endpoint) => {
    return endpoint?.endsWith('/unique');
  }

  renderEndpointGroup = (type, name, endpoints) => {
    return (
      <ListGroup.Item 
        key={`${type}-${name}`}
        className="border-0 py-2"
      >
        <div className="d-flex flex-column">
          <div className="mb-2">
            <strong>{name}</strong>
            <small className="text-muted text-capitalize ms-2">({type})</small>
          </div>
          <div className="d-flex gap-2">
            <Button
              variant={this.state.selectedEndpoint === endpoints.regular ? "primary" : "outline-primary"}
              size="sm"
              onClick={() => this.handleEndpointSelect(endpoints.regular)}
              style={{ flex: 1 }}
            >
              All Items
            </Button>
            <Button
              variant={this.state.selectedEndpoint === endpoints.unique ? "primary" : "outline-primary"}
              size="sm"
              onClick={() => this.handleEndpointSelect(endpoints.unique)}
              style={{ flex: 1 }}
            >
              New Items
            </Button>
          </div>
        </div>
      </ListGroup.Item>
    );
  }

  render() {
    const { 
      endpoints, 
      selectedEndpoint, 
      isLoading, 
      error, 
      commitInProgress,
      previewMode 
    } = this.state;
    const groupedEndpoints = this.groupEndpoints(endpoints);

    return (
      <div className="container-fluid px-4">
        <h2 className="mb-4">RSS Endpoints</h2>

        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        {isLoading ? (
          <div className="text-center py-3">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div className="row g-4">
            <div className="col-md-4">
              {Object.entries(groupedEndpoints).map(([type, typeEndpoints]) => (
                <Card key={type} className={`mb-3 card-${type === 'source' ? 'primary' : 'secondary'}`}>
                  <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-uppercase fw-bold">
                        {type} Feeds
                      </span>
                    </div>
                  </Card.Header>
                  <ListGroup variant="flush">
                    {Object.entries(typeEndpoints).length === 0 ? (
                      <ListGroup.Item className="text-muted">
                        No {type}s available
                      </ListGroup.Item>
                    ) : (
                      Object.entries(typeEndpoints).map(([name, endpoints]) => (
                        <ListGroup.Item 
                          key={`${type}-${name}`}
                          className="border-0 py-2"
                        >
                          <div className="d-flex flex-column">
                            <div className="mb-2">
                              <strong>{name}</strong>
                              <small className="text-muted text-capitalize ms-2">({type})</small>
                            </div>
                            <div className="d-flex gap-2">
                              <Button
                                variant={selectedEndpoint === endpoints.urls.all ? "primary" : "outline-primary"}
                                size="sm"
                                onClick={() => this.handleEndpointSelect(endpoints.urls.all)}
                                style={{ flex: 1 }}
                              >
                                All Items
                              </Button>
                              <Button
                                variant={selectedEndpoint === endpoints.urls.unique ? "primary" : "outline-primary"}
                                size="sm"
                                onClick={() => this.handleEndpointSelect(endpoints.urls.unique)}
                                style={{ flex: 1 }}
                              >
                                New Items
                              </Button>
                            </div>
                          </div>
                        </ListGroup.Item>
                      ))
                    )}
                  </ListGroup>
                </Card>
              ))}
            </div>
            <div className="col-md-8">
              {selectedEndpoint ? (
                <Card>
                  <Card.Header className="bg-light">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-uppercase fw-bold">
                        Feed Preview
                        {this.isUniqueEndpoint(selectedEndpoint) && previewMode && (
                          <span className="ms-2 badge bg-info">Preview Mode</span>
                        )}
                      </span>
                      <div>
                        {this.isUniqueEndpoint(selectedEndpoint) && previewMode && (
                          <Button 
                            variant="success"
                            size="sm"
                            onClick={this.handleCommit}
                            disabled={commitInProgress}
                            className="me-2"
                          >
                            {commitInProgress ? 'Committing...' : 'Refresh and Commit'}
                          </Button>
                        )}
                        <Button 
                          variant="outline-secondary" 
                          size="sm"
                          href={buildRssUrl(selectedEndpoint)}
                          target="_blank"
                          className="me-2"
                        >
                          View Raw
                        </Button>
                        <Button 
                          variant="outline-secondary" 
                          size="sm"
                          onClick={() => this.setState({ 
                            selectedEndpoint: null,
                            previewMode: true  // Reset to preview mode when closing
                          })}
                        >
                          Close
                        </Button>
                      </div>
                    </div>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <RSSFeedView 
                      url={selectedEndpoint}
                      preview={previewMode}
                      ref={this.feedViewRef}
                    />
                  </Card.Body>
                </Card>
              ) : (
                <div className="text-center py-5 text-muted">
                  <h5 className="fw-normal">Select an endpoint to view its feed</h5>
                  <p className="small">Click on any endpoint from the list to preview its RSS feed</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default Endpoints;